import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format } from "date-fns";
import {
  Mail, Upload, Globe, Send, Calendar as CalendarIcon, BarChart3,
  LogOut, Plus, Eye, Trash2, RefreshCw, Users, MousePointerClick,
  MailOpen, Clock, CheckCircle2, AlertCircle, Loader2, FileSpreadsheet,
  ChevronRight, ChevronLeft, Sparkles, LayoutDashboard, History, Settings, Menu, X,
  Minus, PlusCircle, EyeOff, ChevronDown, ChevronUp, Image as ImageIcon, Palette, Share2, Copy, Download, Facebook, Instagram,
  HelpCircle, Maximize2, Minimize2, AlertTriangle, MessageSquare
} from "lucide-react";
import { ImagePicker } from "@/components/ImagePicker";
import { SectionImageManager } from "@/components/SectionImageManager";
import { GettingStartedPage, GettingStartedChecklist, TroubleshootingHelp } from "@/components/GettingStartedGuide";
import { useAuth, API, LoadingScreen } from "@/contexts/AuthContext";
import { Layout } from "@/components/layout/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { authAxios } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  const [billing, setBilling] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('sse_welcome_seen');
    if (!hasSeenWelcome) {
      setIsFirstVisit(true);
      localStorage.setItem('sse_welcome_seen', 'true');
    }

    Promise.all([
      authAxios.get("/campaigns").then(r => r.data).catch(() => []),
      authAxios.get("/billing/status").then(r => r.data).catch(() => null),
    ]).then(([camps, bill]) => {
      setAllCampaigns(camps);
      setCampaigns(camps.slice(0, 5));
      setBilling(bill);
    }).finally(() => setLoading(false));
  }, [authAxios]);

  // "What needs your attention" — derive momentum signals from campaigns
  const drafts = allCampaigns.filter(c => c.status === "draft");
  const scheduled = allCampaigns.filter(c => c.status === "scheduled");
  const sent = allCampaigns
    .filter(c => c.status === "sent" && c.sent_at)
    .sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at));
  const lastSent = sent[0] || null;
  const daysSinceLastSent = lastSent
    ? Math.floor((Date.now() - new Date(lastSent.sent_at)) / (1000 * 60 * 60 * 24))
    : null;
  // Next scheduled — earliest future scheduled_at
  const upcomingScheduled = [...scheduled]
    .filter(c => c.scheduled_at)
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0] || null;

  if (loading) return <Layout><LoadingScreen /></Layout>;

  return (
    <Layout>
      <div className="p-6 lg:p-10" data-testid="dashboard">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight" style={{fontFamily: 'Manrope'}}>
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Welcome back. Here's where you left off.</p>
        </div>

        {/* Trial / billing banner */}
        {billing && (billing.subscription_status === "trialing" || billing.subscription_status === "none" || billing.subscription_status === "canceled") && (
          <div
            data-testid="dashboard-trial-banner"
            className={`mb-6 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${
              billing.subscription_status === "trialing"
                ? "bg-[#B7EB1B]/10 border border-[#B7EB1B]/40"
                : "bg-amber-50 border border-amber-200"
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                billing.subscription_status === "trialing" ? "bg-[#B7EB1B]/30" : "bg-amber-200"
              }`}>
                {billing.subscription_status === "trialing"
                  ? <Sparkles className="w-5 h-5 text-[#0a1e3a]" />
                  : <AlertTriangle className="w-5 h-5 text-amber-700" />}
              </div>
              <div className="text-sm">
                {billing.subscription_status === "trialing" ? (
                  <>
                    <span className="font-bold text-slate-900">
                      {billing.trial_days_remaining === 0
                        ? "Your free trial ends today."
                        : `${billing.trial_days_remaining} day${billing.trial_days_remaining === 1 ? '' : 's'} left in your free trial.`}
                    </span>
                    {!billing.has_payment_method && (
                      <span className="text-slate-700"> Add a card anytime to keep going.</span>
                    )}
                  </>
                ) : (
                  <>
                    <span className="font-bold text-slate-900">Start your 14-day free trial</span>
                    <span className="text-slate-700"> to send emails to your contact list. No credit card needed.</span>
                  </>
                )}
              </div>
            </div>
            <Button
              onClick={() => navigate(billing.subscription_status === "trialing" ? "/billing" : "/pricing")}
              data-testid="dashboard-trial-cta"
              className={`rounded-full font-semibold ${
                billing.subscription_status === "trialing"
                  ? "bg-[#0a1e3a] hover:bg-[#0a1e3a]/90 text-white"
                  : "bg-amber-600 hover:bg-amber-700 text-white"
              }`}
            >
              {billing.subscription_status === "trialing" ? "Manage" : "Start Free Trial"} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* How Simply Smarter Email Works — collapsible.
            Expanded on first visit, collapsed for returning users. */}
        <div data-testid="dashboard-getting-started">
          <GettingStartedChecklist defaultExpanded={isFirstVisit} />
        </div>

        {/* Action row: Create New + What needs your attention */}
        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          {/* Create New Campaign */}
          <Card className="bg-gradient-to-br from-[#003858] to-[#119AB3] border-0 rounded-2xl text-white">
            <CardContent className="p-6">
              <Sparkles className="w-10 h-10 mb-4 text-[#B7EB1B]" />
              <h3 className="text-xl font-bold mb-2" style={{fontFamily: 'Manrope'}}>Create New Campaign</h3>
              <p className="text-white/80 mb-4">Paste a URL. We'll handle the rest.</p>
              <Button
                data-testid="create-campaign-btn"
                onClick={() => navigate("/campaign/new")}
                className="bg-[#B7EB1B] hover:bg-[#B7EB1B]/90 text-[#0a1e3a] rounded-full font-semibold"
              >
                Get Started <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>

          {/* What needs your attention */}
          <Card className="lg:col-span-2 bg-white border-slate-200 rounded-2xl shadow-sm" data-testid="dashboard-attention">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-[#B7EB1B]/15 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-[#0d9488]" />
                </div>
                <h3 className="text-lg font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                  What needs your attention
                </h3>
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                {/* Drafts */}
                <button
                  onClick={() => navigate("/campaigns?filter=draft")}
                  data-testid="attention-drafts"
                  className="text-left p-4 rounded-xl border border-slate-200 hover:border-[#0d9488] hover:bg-slate-50 transition-all group"
                >
                  <div className="text-3xl font-extrabold text-slate-900" style={{fontFamily: 'Manrope'}}>
                    {drafts.length}
                  </div>
                  <div className="text-sm font-semibold text-slate-700 mt-1">
                    {drafts.length === 1 ? "Draft in progress" : "Drafts in progress"}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 group-hover:text-[#0d9488] transition-colors">
                    {drafts.length === 0 ? "Nothing waiting" : "Pick up where you left off →"}
                  </div>
                </button>

                {/* Scheduled */}
                <button
                  onClick={() => navigate("/campaigns?filter=scheduled")}
                  data-testid="attention-scheduled"
                  className="text-left p-4 rounded-xl border border-slate-200 hover:border-[#0d9488] hover:bg-slate-50 transition-all group"
                >
                  <div className="text-3xl font-extrabold text-slate-900" style={{fontFamily: 'Manrope'}}>
                    {scheduled.length}
                  </div>
                  <div className="text-sm font-semibold text-slate-700 mt-1">
                    {scheduled.length === 1 ? "Scheduled to send" : "Scheduled to send"}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 group-hover:text-[#0d9488] transition-colors">
                    {upcomingScheduled
                      ? `Next: ${new Date(upcomingScheduled.scheduled_at).toLocaleDateString(undefined, {month: 'short', day: 'numeric'})}`
                      : "Nothing on the calendar"}
                  </div>
                </button>

                {/* Last sent / nudge */}
                <button
                  onClick={() => navigate(lastSent ? `/campaign/${lastSent.id}` : "/campaign/new")}
                  data-testid="attention-momentum"
                  className={`text-left p-4 rounded-xl border transition-all group ${
                    daysSinceLastSent !== null && daysSinceLastSent >= 14
                      ? "border-amber-300 bg-amber-50 hover:bg-amber-100"
                      : "border-slate-200 hover:border-[#0d9488] hover:bg-slate-50"
                  }`}
                >
                  <div className="text-3xl font-extrabold text-slate-900" style={{fontFamily: 'Manrope'}}>
                    {daysSinceLastSent === null ? "—" : daysSinceLastSent === 0 ? "Today" : `${daysSinceLastSent}d`}
                  </div>
                  <div className="text-sm font-semibold text-slate-700 mt-1">
                    {lastSent ? "Since last send" : "No emails sent yet"}
                  </div>
                  <div className={`text-xs mt-1 transition-colors ${
                    daysSinceLastSent !== null && daysSinceLastSent >= 14 ? "text-amber-700 font-medium" : "text-slate-500 group-hover:text-[#0d9488]"
                  }`}>
                    {!lastSent
                      ? "Send your first one →"
                      : daysSinceLastSent >= 14
                        ? "Time to send something →"
                        : `"${lastSent.name}"`}
                  </div>
                </button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Campaigns */}
        <Card className="bg-white border-slate-200 rounded-2xl shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl" style={{fontFamily: 'Manrope'}}>Recent Campaigns</CardTitle>
            {allCampaigns.length > 5 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/campaigns")}
                className="text-sm text-slate-500 hover:text-[#0d9488]"
                data-testid="dashboard-see-all-campaigns"
              >
                See all <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {campaigns.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No campaigns yet. Create your first one!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {campaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    onClick={() => navigate(`/campaign/${campaign.id}`)}
                    className="flex items-center justify-between p-4 rounded-xl bg-slate-50 hover:bg-afa-navy-light cursor-pointer transition-colors"
                    data-testid={`campaign-${campaign.id}`}
                  >
                    <div>
                      <p className="font-semibold text-slate-900">{campaign.name}</p>
                      <p className="text-sm text-slate-500">{campaign.email_list?.length || 0} recipients</p>
                    </div>
                    <Badge className={`status-${campaign.status}`}>{campaign.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

// New Campaign Wizard

export default Dashboard;
